/** A point on the stage in pixels. */
export type Point = readonly [number, number];

/**
 * CSS `matrix3d` that maps an element of `w` x `h` pixels (transform-origin at its top left) onto
 * the quadrilateral `quad` (top left, top right, bottom right, bottom left): a perspective
 * (projective) transform, for content shown on a screen standing at an angle in a painting.
 */
export function quadMatrix(
  w: number,
  h: number,
  quad: readonly [Point, Point, Point, Point],
): string {
  const [[x0, y0], [x1, y1], [x2, y2], [x3, y3]] = quad;
  // The unit square (0,0) (1,0) (1,1) (0,1) to the quad (Heckbert's square-to-quad mapping).
  const dx1 = x1 - x2;
  const dx2 = x3 - x2;
  const dx3 = x0 - x1 + x2 - x3;
  const dy1 = y1 - y2;
  const dy2 = y3 - y2;
  const dy3 = y0 - y1 + y2 - y3;
  const det = dx1 * dy2 - dx2 * dy1;
  const g = det === 0 ? 0 : (dx3 * dy2 - dx2 * dy3) / det;
  const k = det === 0 ? 0 : (dx1 * dy3 - dx3 * dy1) / det;
  const a = x1 - x0 + g * x1;
  const b = x3 - x0 + k * x3;
  const d = y1 - y0 + g * y1;
  const e = y3 - y0 + k * y3;
  // Pixels of the element to the unit square, then the square to the quad; column-major.
  const m = [a / w, d / w, 0, g / w, b / h, e / h, 0, k / h, 0, 0, 1, 0, x0, y0, 0, 1];
  return `matrix3d(${m.map((v) => String(Number.isFinite(v) ? v : 0)).join(',')})`;
}

/** The quad's mean width and height: the element's size, so its content keeps its proportions. */
export function quadSize(quad: readonly [Point, Point, Point, Point]): [number, number] {
  const [tl, tr, br, bl] = quad;
  const length = (p: Point, q: Point) => Math.hypot(q[0] - p[0], q[1] - p[1]);
  return [(length(tl, tr) + length(bl, br)) / 2, (length(tl, bl) + length(tr, br)) / 2];
}
